// v2 — Bot Management exceptions, redesigned to match the WAF
// exception screen's IA and drawer pattern:
//
//   * List screen mirrors `ConfigWAFExceptTabContent.js`:
//     search box (left) + Add button (right) toolbar; bordered
//     table inside an 8px-radius Box; action-icon → Menu →
//     Edit / Delete; centered Modal for delete confirmation.
//
//   * Edit/Create slides in from the right edge mirroring
//     `ConfigWAFExceptionSettings.js`: 33%-width Modal + Slide,
//     header with icon tile + title + description + close,
//     ReactPerfectScrollbar body, sticky footer with Cancel + Save.
//
// Data model stays unchanged — CIDRs, UIDs, JA3, JA4, Path globs,
// Countries, ASNs, Headers, expires_at — and is still PATCHed to
// `/config/bot/:siteId/bm-exceptions/:id` (POST for create).

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { validateBmExceptionForm } from "../../../../../utils/validation";
import { useDrawerBaselineDirty, useGuardedDrawerClose } from "../../super/design";
import { Helmet } from "react-helmet-async";
import {
  Stack, Box, Typography, Grid, Button, IconButton,
  Table, TableBody, TableCell, TableHead, TableRow, OutlinedInput,
  Modal, Slide, Menu, Chip, Autocomplete, LinearProgress,
  Select, InputLabel, MenuItem} from "@mui/material";
import ReactPerfectScrollbar from "react-perfect-scrollbar";
import LayerdIcon from "../../../../common/LayerdIcon";
import { UI_ICONS } from "../../../../../icons/uiLucideIcons";
import { ReactComponent as CloseIcon } from "../../../../../vendor/_new/icon/navbar/notification/close_big.svg";
import { ReactComponent as InfoIcon } from "../../../../../vendor/_new/icon/sites/info.svg";
import { ReactComponent as ExceptIcon } from "../../../../../vendor/_new/icon/services/except.svg";
import { ReactComponent as DeleteIcon } from "../../../../../vendor/_new/icon/services/delete.svg";
import { ReactComponent as PlusIcon } from "../../../../../vendor/_new/icon/sites/plus.svg";
import { ReactComponent as ArrowdownIcon } from "../../../../../vendor/_new/icon/sites/arrow_down.svg";
import { ReactComponent as DuplicateIcon } from "../../../../../vendor/_new/icon/services/duplicate.svg";

import { tableHeadSx, tableBodySx } from "../../detection/WAFEvent";
import { GreenSwitch, SafeTextField, GreenSelectForm, GreenDateTimeLocalField } from "../../../../common/Greens";
import { FireWallExpressionCondition } from "../../../../../utils/constants";
import { CancelButton, SaveButton, RefreshButton, PrimaryButton, containedButtonSx } from "../../../../common/Button";
import FormHelpTooltip from "../../../../common/FormHelpTooltip";
import { generateMenuSx } from "../../../../../layouts/_new/Normal";
import colors from "../../../../../utils/colors";
import V2PageHeader from "../../../../common/V2PageHeader";
import SiteScopeGate from "../../../../common/SiteScopeGate";
import userAxios from "../../../../../utils/axios/v1/userAxios";
import useAuth from "../../../../../hooks/useAuth";
import useSite from "../../../../../hooks/user/useSite";
import useGlobalSite from "../../../../../hooks/user/useGlobalSite";
import { useNotification } from "../../../../../contexts/NotificationContext";
import StickySaveBar from "../../../../common/StickySaveBar";
import { cloneItems, collectRuleEnabledDiff } from "../../../../common/stickySaveBarUtils";
import countries from "../../../application/rule/countries";

// ── Drawer-form primitives (match ConfigWAFExceptionSettings styling) ──

const helperTextSx = { fontSize: 12, color: colors.neutral[600], mx: 0, mt: 0.5 };

const menuItemSx = {
  height: 38,
  fontSize: 14,
  lineHeight: "21px",
  "&:hover": { color: colors.primary[300] },
};

const BM_FIELD_NONE = "none";
const BM_OPERATOR_NONE = "none";

const BM_MATCH_FIELDS = [
  { key: "cidrs", label: "CIDR", helper: "IPv4 or IPv6 CIDR blocks, e.g. 203.0.113.0/24" },
  { key: "uids", label: "UID", helper: "Bot Management UID cookie values" },
  { key: "ja3s", label: "JA3 fingerprint", helper: "TLS JA3 fingerprint (32 hex chars)" },
  { key: "ja4s", label: "JA4 fingerprint", helper: "TLS JA4 fingerprint hash" },
  { key: "paths", label: "Path glob", helper: "URI path patterns, e.g. /admin/**" },
  { key: "asns", label: "ASN", helper: "Autonomous System Numbers, e.g. 64512", numeric: true },
  { key: "countries", label: "Country", helper: "ISO country code" },
  { key: "headers", label: "Header", helper: "Header name and glob pattern" },
];

const EMPTY_MATCH_ROW = () => ({
  field: BM_FIELD_NONE,
  operator: BM_OPERATOR_NONE,
  value: "",
  headerName: "",
  headerGlob: "",
});

function exceptionToMatchRows(exception) {
  const rows = [];
  if (!exception) return [EMPTY_MATCH_ROW()];

  for (const { key } of BM_MATCH_FIELDS) {
    if (key === "headers") {
      for (const h of exception.headers || []) {
        if (!h?.name) continue;
        rows.push({
          field: "headers",
          operator: FireWallExpressionCondition.EQUALS,
          value: "",
          headerName: h.name,
          headerGlob: h.value_glob || "",
        });
      }
      continue;
    }
    if (key === "countries") {
      for (const code of exception.countries || []) {
        rows.push({
          field: "countries",
          operator: FireWallExpressionCondition.EQUALS,
          value: String(code).toUpperCase(),
          headerName: "",
          headerGlob: "",
        });
      }
      continue;
    }
    if (key === "asns") {
      for (const asn of exception.asns || []) {
        rows.push({
          field: "asns",
          operator: FireWallExpressionCondition.EQUALS,
          value: String(asn),
          headerName: "",
          headerGlob: "",
        });
      }
      continue;
    }
    for (const v of exception[key] || []) {
      if (!v) continue;
      rows.push({
        field: key,
        operator: FireWallExpressionCondition.EQUALS,
        value: String(v),
        headerName: "",
        headerGlob: "",
      });
    }
  }

  return rows.length ? rows : [EMPTY_MATCH_ROW()];
}

function matchRowsToCriteria(rows) {
  const result = {
    cidrs: [],
    uids: [],
    ja3s: [],
    ja4s: [],
    paths: [],
    countries: [],
    asns: [],
    headers: [],
  };

  for (const row of rows) {
    if (!row.field || row.field === BM_FIELD_NONE) continue;

    if (row.field === "headers") {
      const name = (row.headerName || "").trim();
      if (name) {
        result.headers.push({ name, value_glob: row.headerGlob || "" });
      }
      continue;
    }

    if (row.field === "countries") {
      const code = (row.value || "").trim().toUpperCase();
      if (code) result.countries.push(code);
      continue;
    }

    if (row.field === "asns") {
      const n = parseInt(row.value, 10);
      if (Number.isFinite(n)) result.asns.push(n);
      continue;
    }

    const v = (row.value || "").trim();
    if (v) result[row.field].push(v);
  }

  result.cidrs = Array.from(new Set(result.cidrs));
  result.uids = Array.from(new Set(result.uids));
  result.ja3s = Array.from(new Set(result.ja3s));
  result.ja4s = Array.from(new Set(result.ja4s));
  result.paths = Array.from(new Set(result.paths));
  result.countries = Array.from(new Set(result.countries));
  result.asns = Array.from(new Set(result.asns));

  return result;
}

function BmCountryValue({ value, onChange, disabled }) {
  const selected = countries.find((c) => c.value === String(value || "").toUpperCase()) || null;
  return (
    <Autocomplete
      fullWidth
      options={countries}
      autoHighlight
      disabled={disabled}
      popupIcon={<ArrowdownIcon />}
      getOptionLabel={(o) => `${o.label} (${o.value})`}
      isOptionEqualToValue={(o, v) => o.value === v.value}
      value={selected}
      onChange={(_, option) => onChange(option?.value || "")}
      renderOption={(props, option) => (
        <Box
          component="li"
          {...props}
          sx={{ fontSize: 14, lineHeight: "21px", color: colors.neutral[1000] }}
        >
          {option.label} ({option.value})
        </Box>
      )}
      renderInput={(params) => (
        <SafeTextField
          {...params}
          label="Value"
          placeholder="Select a country"
          sx={{ fontSize: 14, lineHeight: "21px", color: colors.neutral[1000] }}
        />
      )}
    />
  );
}

function MatchConditionsField({ rows, setRows, rowErrors = [], touched = {}, onTouchField }) {
  const updateRow = (index, patch) => {
    setRows(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const rowTouched = (index, field) => touched[`${index}.${field}`];
  const showErr = (index, field) => rowTouched(index, field) && rowErrors[index]?.[field];

  const addRow = () => setRows([...rows, EMPTY_MATCH_ROW()]);

  const removeRow = (index) => {
    if (rows.length <= 1) {
      setRows([EMPTY_MATCH_ROW()]);
      return;
    }
    setRows(rows.filter((_, i) => i !== index));
  };

  const fieldMeta = (key) => BM_MATCH_FIELDS.find((f) => f.key === key);

  return (
    <Box sx={{ width: "100%", p: 4, borderRadius: "8px", border: `solid 1px ${colors.neutral[200]}` }}>
      {rows.map((row, index) => {
        const meta = fieldMeta(row.field);
        const fieldSelected = row.field && row.field !== BM_FIELD_NONE;
        const isHeader = row.field === "headers";
        const isCountry = row.field === "countries";
        const valueDisabled = !fieldSelected || row.operator === BM_OPERATOR_NONE;

        return (
          <Box key={index} sx={{ mb: index < rows.length - 1 ? 3 : 0 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography sx={{ fontSize: 16, lineHeight: "24px", color: colors.neutral[1000] }}>
                  {index === 0 ? "When incoming requests match…" : "And"}
                </Typography>
                {index === 0 && (
                  <Typography sx={{ fontSize: 12, lineHeight: "18px", color: colors.neutral[600], mt: 1 }}>
                    AND across rows. Each row adds one match criterion.
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <GreenSelectForm error={Boolean(showErr(index, "field"))}>
                  <InputLabel id={`bm-field-${index}`}>Field</InputLabel>
                  <Select
                    labelId={`bm-field-${index}`}
                    fullWidth
                    IconComponent={ArrowdownIcon}
                    value={row.field}
                    onBlur={() => onTouchField?.(index, "field")}
                    onChange={(e) => {
                      const nextField = e.target.value;
                      updateRow(index, {
                        field: nextField,
                        operator: nextField === BM_FIELD_NONE ? BM_OPERATOR_NONE : FireWallExpressionCondition.EQUALS,
                        value: "",
                        headerName: "",
                        headerGlob: "",
                      });
                    }}
                  >
                    {row.field === BM_FIELD_NONE && (
                      <MenuItem value={BM_FIELD_NONE} disabled sx={{ ...menuItemSx, display: "none!important" }}>
                        Select Field ...
                      </MenuItem>
                    )}
                    {BM_MATCH_FIELDS.map((f) => (
                      <MenuItem key={f.key} value={f.key} sx={menuItemSx}>
                        {f.label}
                      </MenuItem>
                    ))}
                  </Select>
                </GreenSelectForm>
              </Grid>
              <Grid item xs={12} sm={6}>
                <GreenSelectForm error={Boolean(showErr(index, "operator"))}>
                  <InputLabel id={`bm-operator-${index}`}>Operator</InputLabel>
                  <Select
                    labelId={`bm-operator-${index}`}
                    fullWidth
                    IconComponent={ArrowdownIcon}
                    disabled={!fieldSelected}
                    value={fieldSelected ? row.operator : BM_OPERATOR_NONE}
                    onBlur={() => onTouchField?.(index, "operator")}
                    onChange={(e) => updateRow(index, { operator: e.target.value })}
                  >
                    {row.operator === BM_OPERATOR_NONE && (
                      <MenuItem value={BM_OPERATOR_NONE} disabled sx={{ ...menuItemSx, display: "none!important" }}>
                        Select Item ...
                      </MenuItem>
                    )}
                    <MenuItem value={FireWallExpressionCondition.EQUALS} sx={menuItemSx}>
                      Equals
                    </MenuItem>
                  </Select>
                </GreenSelectForm>
              </Grid>
              <Grid item xs={12}>
                {isHeader ? (
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <SafeTextField
                        fullWidth
                        sanitize={false}
                        label="Header name"
                        value={row.headerName}
                        disabled={valueDisabled}
                        error={Boolean(showErr(index, "headerName"))}
                        helperText={showErr(index, "headerName") || ""}
                        onBlur={() => onTouchField?.(index, "headerName")}
                        onChange={(e) => updateRow(index, { headerName: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <SafeTextField
                        fullWidth
                        sanitize={false}
                        label="Value glob"
                        value={row.headerGlob}
                        disabled={valueDisabled}
                        error={Boolean(showErr(index, "headerGlob"))}
                        helperText={showErr(index, "headerGlob") || meta?.helper}
                        FormHelperTextProps={{ sx: helperTextSx }}
                        onBlur={() => onTouchField?.(index, "headerGlob")}
                        onChange={(e) => updateRow(index, { headerGlob: e.target.value })}
                      />
                    </Grid>
                  </Grid>
                ) : isCountry ? (
                  <Box onBlur={() => onTouchField?.(index, "value")}>
                    <BmCountryValue
                      value={row.value}
                      disabled={valueDisabled}
                      onChange={(v) => updateRow(index, { value: v })}
                    />
                    {showErr(index, "value") && (
                      <Typography sx={{ ...helperTextSx, color: colors.red?.[500] || "#d32f2f", mt: 0.5 }}>
                        {rowErrors[index].value}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <SafeTextField
                    fullWidth
                    label="Value"
                    type={meta?.numeric ? "number" : "text"}
                    value={row.value}
                    disabled={valueDisabled}
                    error={Boolean(showErr(index, "value"))}
                    helperText={showErr(index, "value") || meta?.helper}
                    FormHelperTextProps={{ sx: helperTextSx }}
                    onBlur={() => onTouchField?.(index, "value")}
                    onChange={(e) => updateRow(index, { value: e.target.value })}
                  />
                )}
              </Grid>
              <Grid item xs={12}>
                <Stack width="100%" alignItems="center" direction="row" justifyContent="flex-end" spacing={2}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    onClick={addRow}
                    sx={{
                      cursor: "pointer",
                      borderRadius: "8px",
                      border: `solid 1px ${colors.secondary[300]}`,
                      px: 2,
                      py: 1,
                    }}
                  >
                    <DuplicateIcon style={{ color: colors.neutral[800] }} />
                    <Typography sx={{ fontSize: 14, lineHeight: "21px", color: colors.neutral[800] }}>
                      And
                    </Typography>
                  </Stack>
                  <Box onClick={() => removeRow(index)} sx={{ cursor: "pointer", display: "flex" }}>
                    <DeleteIcon style={{ color: colors.neutral[600] }} />
                  </Box>
                </Stack>
              </Grid>
              {index === rows.length - 1 && (
                <Grid item xs={12}>
                  <Button
                    startIcon={<PlusIcon style={{ color: colors.neutral[100] }} />}
                    onClick={addRow}
                    sx={{ height: 32, width: 105, ...containedButtonSx }}
                  >
                    Add new
                  </Button>
                </Grid>
              )}
            </Grid>
          </Box>
        );
      })}
    </Box>
  );
}

/** Local `datetime-local` value (BM drawer only — does not change other modules). */
function toLocalDateTimeLocal(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Prefill when expiry is missing/invalid: now + 7 days (local). */
function defaultExpiresAtPlus7Days() {
  return toLocalDateTimeLocal(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000));
}

function hydrateExpiresAt(iso) {
  if (!iso) return defaultExpiresAtPlus7Days();
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return defaultExpiresAtPlus7Days();
  return toLocalDateTimeLocal(d);
}

// ── Drawer ───────────────────────────────────────────────────────
function ExceptionDrawer({ open, onClose, exceptionToEdit, onSubmit }) {
  const isEdit = !!exceptionToEdit?.id;
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [matchRows, setMatchRows] = useState([EMPTY_MATCH_ROW()]);
  const [expiresAt, setExpiresAt] = useState("");
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [baselineKey, setBaselineKey] = useState(0);

  const validation = useMemo(
    () => validateBmExceptionForm({ name, description, matchRows, expiresAt }),
    [name, description, matchRows, expiresAt],
  );

  const touchField = (index, field) => {
    setTouched((prev) => ({ ...prev, [`${index}.${field}`]: true }));
  };

  const touchFormField = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const showFormErr = (field) => (submitAttempted || touched[field]) && validation.formErrors[field];

  // Hydrate on open / re-open. Reset state every time so a fresh
  // "Add" never leaks values from a prior edit.
  useEffect(() => {
    if (!open) return;
    setName(exceptionToEdit?.name || "");
    setDescription(exceptionToEdit?.description || "");
    setEnabled(exceptionToEdit ? exceptionToEdit.enabled !== false : true);
    setMatchRows(exceptionToMatchRows(exceptionToEdit));
    setExpiresAt(hydrateExpiresAt(exceptionToEdit?.expires_at));
    setTouched({});
    setSubmitAttempted(false);
    setBaselineKey((k) => k + 1);
  }, [open, exceptionToEdit]);

  const formSnapshot = useMemo(
    () => ({ name, description, enabled, matchRows, expiresAt }),
    [name, description, enabled, matchRows, expiresAt],
  );
  const isDirty = useDrawerBaselineDirty(open, formSnapshot, baselineKey);
  const { requestClose, confirmDialog } = useGuardedDrawerClose(onClose, isDirty);

  const submit = () => {
    setSubmitAttempted(true);
    const result = validateBmExceptionForm({ name, description, matchRows, expiresAt });
    if (!result.valid) return;

    const criteria = matchRowsToCriteria(matchRows);
    onSubmit({
      name,
      description,
      enabled,
      ...criteria,
      countries: criteria.countries.map((c) => String(c).toUpperCase()),
      headers: criteria.headers.filter((h) => h.name),
      expires_at: new Date(expiresAt).toISOString(),
    });
  };

  return (
    <>
    <Modal
      open={open}
      onClose={requestClose}
      slotProps={{ backdrop: { sx: { backgroundColor: "rgba(0,0,0,0.5)" } } }}
    >
      <Slide direction="left" in={open} mountOnEnter unmountOnExit>
        <Stack
          sx={{
            position: "fixed",
            top: 0,
            right: 0,
            width: "33%",
            minWidth: 480,
            height: "100vh",
            backgroundColor: "#fff",
            boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
            zIndex: 1300,
            borderTopLeftRadius: "8px",
          }}
        >
          <Stack direction="row" justifyContent="space-between" spacing={6} sx={{ px: 4, pt: 4, pb: 3 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box sx={{
                width: 48, height: 48, borderRadius: "8px",
                backgroundColor: "#EEF0FE",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <ExceptIcon style={{ width: 32, height: 32, color: "#6371F7" }} />
              </Box>
              <Stack>
                <Stack direction="row" alignItems="center" sx={{ flexWrap: "nowrap" }}>
                  <Typography sx={ { fontWeight: "medium", fontSize: 20, lineHeight: "30px", color: colors.neutral[1000] }}>
                    {isEdit ? "Edit BM Exception" : "Add BM Exception"}
                  </Typography>
                  <FormHelpTooltip tooltip="Allowlist paths or expressions so Bot Management skips scoring and enforcement for matching traffic. Use for trusted crawlers or partner integrations." />
                </Stack>
                <Typography sx={ { fontSize: 12, lineHeight: "18px", color: colors.neutral[700] }}>
                  Create an allowlist rule so Bot Management treats matching requests as trusted.
                </Typography>
              </Stack>
            </Stack>
            <IconButton onClick={requestClose} aria-label="Close" sx={{ p: 0 }}>
              <CloseIcon sx={{ color: colors.neutral[600] }} />
            </IconButton>
          </Stack>

          <Box sx={{ py: 4, pl: 4, height: "calc(100vh - 130px)" }}>
            <ReactPerfectScrollbar>
              <Stack justifyContent="space-between" spacing={4} sx={{ pr: 4, height: "100%" }}>
                <Stack spacing={4} key={exceptionToEdit?.id || "new"}>
                  <SafeTextField
                    fullWidth
                    label="Exception name"
                    value={name}
                    error={Boolean(showFormErr("name"))}
                    helperText={showFormErr("name") || ""}
                    onBlur={() => touchFormField("name")}
                    onChange={(e) => setName(e.target.value)}
                  />

                  <SafeTextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Description"
                    value={description}
                    error={Boolean(showFormErr("description"))}
                    helperText={showFormErr("description") || ""}
                    onBlur={() => touchFormField("description")}
                    onChange={(e) => setDescription(e.target.value)}
                  />

                  <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: "flex", alignItems: "center", minHeight: 48, gap: 2 }}>
                        <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] }}>
                          Enabled
                        </Typography>
                        <GreenSwitch checked={enabled} onChange={() => setEnabled(!enabled)} />
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} sx={{ pl: "1px", pr: "12px" }}>
                      <GreenDateTimeLocalField
                        fullWidth
                        label="Expires at"
                        disablePast
                        value={expiresAt}
                        error={Boolean(showFormErr("expiresAt"))}
                        helperText={showFormErr("expiresAt") || ""}
                        onBlur={() => touchFormField("expiresAt")}
                        onChange={(e) => setExpiresAt(e.target.value)}
                      />
                    </Grid>
                  </Grid>

                  <MatchConditionsField
                    rows={matchRows}
                    setRows={setMatchRows}
                    rowErrors={validation.rowErrors}
                    touched={submitAttempted ? Object.fromEntries(matchRows.flatMap((_, i) =>
                      ["field", "operator", "value", "headerName", "headerGlob"].map((f) => [`${i}.${f}`, true])
                    )) : touched}
                    onTouchField={touchField}
                  />
                  {showFormErr("matchRows") && (
                    <Typography sx={{ fontSize: 12, color: colors.red?.[500] || "#d32f2f" }}>
                      {validation.formErrors.matchRows}
                    </Typography>
                  )}

                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                      minHeight: 48,
                      p: 2,
                      borderRadius: "8px",
                      backgroundColor: colors.primary["alpha"],
                      border: `solid 1px ${colors.primary[300]}`,
                    }}
                  >
                    <Box sx={{ width: 24, height: 24, flexShrink: 0 }}>
                      <InfoIcon style={{ color: colors.primary[300], width: 24, height: 24 }} />
                    </Box>
                    <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.primary[300] }}>
                      Matching requests bypass Bot Management scoring. Changes take effect immediately after save.
                    </Typography>
                  </Stack>
                </Stack>

                <Stack direction="row" alignItems="center" spacing={3}>
                  <SaveButton onClick={submit} label={isEdit ? "Save" : "Create"} />
                  <CancelButton onClick={requestClose} />
                </Stack>
              </Stack>
            </ReactPerfectScrollbar>
          </Box>
        </Stack>
      </Slide>
    </Modal>
    {confirmDialog}
    </>
  );
}

const bmExceptionEnabled = (r) => r.enabled !== false;

// ── Page ─────────────────────────────────────────────────────────
const BmExceptionsPage = () => {
  const { isAuthenticated } = useAuth();
  const { siteList, getSitesForItems } = useSite();
  const { showSuccess, showError } = useNotification();

  const { effectiveSiteUid } = useGlobalSite();
  const siteID = effectiveSiteUid;
  const [rows, setRows] = useState(null);
  const [savedRows, setSavedRows] = useState(null);
  const [pattern, setPattern] = useState("");
  const [isInitialized, setIsInitialized] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [actionMenu, setActionMenu] = useState({ anchorEl: null, rowId: null });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSavingToggles, setIsSavingToggles] = useState(false);

  useEffect(() => {
    if (isAuthenticated) getSitesForItems().then(() => setIsInitialized(false));
  }, [isAuthenticated, getSitesForItems]);
  const reload = useCallback(async () => {
    if (!siteID) return;
    try {
      const r = await userAxios.get(`/config/bot/${siteID}/bm-exceptions`);
      const data = Array.isArray(r.data) ? r.data : [];
      setRows(data);
      setSavedRows(data);
    } catch (e) {
      showError(e?.message || String(e));
      setRows([]);
      setSavedRows([]);
    }
  }, [siteID, showError]);
  useEffect(() => { reload(); }, [reload]);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await reload();
    } finally {
      setIsRefreshing(false);
    }
  }, [reload]);

  const submit = async (body) => {
    try {
      if (editing?.id) {
        await userAxios.patch(`/config/bot/${siteID}/bm-exceptions/${editing.id}`, body);
        showSuccess("Exception saved");
      } else {
        await userAxios.post(`/config/bot/${siteID}/bm-exceptions`, body);
        showSuccess("Exception created");
      }
      setDrawerOpen(false);
      setEditing(null);
      reload();
    } catch (e) {
      showError(e?.message || String(e));
    }
  };

  const stickyModifiedFields = useMemo(
    () => collectRuleEnabledDiff(savedRows, rows, undefined, bmExceptionEnabled),
    [savedRows, rows],
  );
  const stickyModifiedCount = stickyModifiedFields.length;

  const stageToggleEnabled = (row, next) => {
    if (isSavingToggles) return;
    setRows((prev) =>
      (prev || []).map((r) => (r.id === row.id ? { ...r, enabled: next } : r)),
    );
  };

  const discardToggleChanges = useCallback(() => {
    setRows(cloneItems(savedRows));
  }, [savedRows]);

  const applyToggleChanges = useCallback(async () => {
    if (!siteID || stickyModifiedCount === 0) return;
    const savedById = Object.fromEntries((savedRows || []).map((r) => [r.id, bmExceptionEnabled(r)]));
    const changed = (rows || []).filter((r) => savedById[r.id] !== bmExceptionEnabled(r));
    if (changed.length === 0) return;

    setIsSavingToggles(true);
    try {
      await Promise.all(
        changed.map((r) => userAxios.patch(`/config/bot/${siteID}/bm-exceptions/${r.id}`, {
          enabled: bmExceptionEnabled(r),
        })),
      );
      setSavedRows(cloneItems(rows));
      showSuccess(`Saved ${changed.length} exception${changed.length === 1 ? "" : "s"}.`);
    } catch (e) {
      showError(e?.message || String(e));
    } finally {
      setIsSavingToggles(false);
    }
  }, [siteID, stickyModifiedCount, savedRows, rows, showError, showSuccess]);

  const handleDelete = async () => {
    if (!deletingId) return;
    try {
      await userAxios.delete(`/config/bot/${siteID}/bm-exceptions/${deletingId}`);
      showSuccess("Exception deleted");
      setDeleteOpen(false); setDeletingId(null);
      reload();
    } catch (e) { showError(e?.message || String(e)); }
  };

  // Same client-side search behaviour as the WAF list: name / id /
  // any criteria-array contents.
  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = pattern.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const hay = [
        r.name, r.description, r.exception_id,
        ...(r.cidrs || []), ...(r.uids || []), ...(r.ja3s || []),
        ...(r.ja4s || []), ...(r.paths || []), ...(r.countries || []),
        ...(r.asns || []).map(String), ...(r.headers || []).map((h) => h.name),
      ].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [rows, pattern]);

  return (
    <>
      <Helmet title="Bot Management — Exception" />
      {isInitialized && (
        <Box sx={{ width: "100%", position: "fixed", top: 66, zIndex: 100 }}>
          <LinearProgress sx={{
            "& .MuiLinearProgress-bar1Indeterminate": { backgroundColor: colors.primary[200] },
            "& .MuiLinearProgress-bar2Indeterminate": { backgroundColor: colors.primary[200] },
            height: 4,
          }} />
        </Box>
      )}
      <Stack spacing={6} sx={{ p: 6 }}>
        <V2PageHeader
          title="Bot Management Exceptions"
          helpTooltip="Bypass bot management for specific paths or clients. Exceptions let legitimate automation through without changing global bot policy."
        />

        <SiteScopeGate mode="single">
          {() => (
            <>
        {/* Search + Refresh + Add */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={3}>
          <OutlinedInput
            startAdornment={<LayerdIcon icon={UI_ICONS.search} size={16} color="neutral" />}
            placeholder="Search name, CIDR, JA4, path…"
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            sx={{
              width: 280, height: 32, borderRadius: "8px",
fontSize: 14, lineHeight: "21px",
              borderColor: colors.neutral[600], pl: 2,
              "& .MuiOutlinedInput-input": { px: 2 },
              "& .MuiInputBase-input::placeholder":  { fontSize: 14, lineHeight: "21px" },
            }}
          />
          <Stack direction="row" alignItems="center" spacing={2}>
            <RefreshButton onClick={handleRefresh} disabled={isRefreshing} loading={isRefreshing} />
            <PrimaryButton
              startIcon={<LayerdIcon icon={UI_ICONS.plus} size={16} color="inherit" />}
              onClick={() => { setEditing(null); setDrawerOpen(true); }}
              sx={{
                height: 40,
                minHeight: 40,
                minWidth: 180,
                px: 2,
                py: 0,
                fontSize: 14,
                lineHeight: "21px",
                fontWeight: 400,
                whiteSpace: "nowrap",
                "& .MuiButton-startIcon": { mr: 1 },
              }}
            >
              Add BM Exception
            </PrimaryButton>
          </Stack>
        </Stack>

        {/* Table */}
        <Box sx={{ width: "100%", borderRadius: "8px", border: `1px solid ${colors.neutral[200]}`, p: 2 }}>
          <Table
            sx={{
              "& .MuiTableCell-head": { py: 3, px: 3 },
              "& .MuiTableCell-body": { py: 2, px: 3 },
              "& .MuiTableCell-root": { borderBottom: "none" },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "8%" }}><Typography sx={tableHeadSx}>No</Typography></TableCell>
                <TableCell sx={{ width: "26%" }}><Typography sx={tableHeadSx}>Name</Typography></TableCell>
                <TableCell sx={{ width: "12%" }}><Typography sx={tableHeadSx}>Scope</Typography></TableCell>
                <TableCell sx={{ width: "30%" }}><Typography sx={tableHeadSx}>Criteria</Typography></TableCell>
                <TableCell sx={{ width: "12%" }}><Typography sx={tableHeadSx}>Expires</Typography></TableCell>
                <TableCell sx={{ width: "8%" }}><Typography sx={tableHeadSx}>Status</Typography></TableCell>
                <TableCell sx={{ width: "4%" }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows === null ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography sx={tableBodySx} textAlign="center">Loading…</Typography>
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography sx={tableBodySx} textAlign="center">
                      {pattern ? "No exceptions match your search." : "There are no exceptions."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filtered.map((r, idx) => {
                const criteria = [
                  r.cidrs?.length && `${r.cidrs.length} CIDR`,
                  r.uids?.length && `${r.uids.length} UID`,
                  r.ja4s?.length && `${r.ja4s.length} JA4`,
                  r.ja3s?.length && `${r.ja3s.length} JA3`,
                  r.paths?.length && `${r.paths.length} path`,
                  r.headers?.length && `${r.headers.length} header`,
                  r.countries?.length && r.countries.join(","),
                  r.asns?.length && `AS${r.asns.join(",")}`,
                ].filter(Boolean);
                return (
                  <TableRow key={r.id}>
                    <TableCell><Typography sx={tableBodySx}>{idx + 1}</Typography></TableCell>
                    <TableCell>
                      <Typography sx={{ ...tableBodySx, fontWeight: 500 }}>{r.name}</Typography>
                      {r.description && (
                        <Typography sx={ { fontSize: 12, color: colors.neutral[600] }}>
                          {r.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.site_id ? (
                        <Chip label="site" size="small" sx={{
fontSize: 11, height: 22,
                          background: colors.neutral[100], color: colors.neutral[700],
                          border: `1px solid ${colors.neutral[200]}`,
                        }} />
                      ) : (
                        <Chip label="global" size="small" sx={{
fontSize: 11, height: 22,
                          background: colors.yellow[10], color: colors.neutral[900],
                          border: `1px solid ${colors.yellow[200]}`,
                        }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {criteria.length === 0 ? (
                          <Typography sx={{ ...tableBodySx, color: colors.neutral[500] }}>—</Typography>
                        ) : criteria.map((c, i) => (
                          <Chip key={i} label={c} size="small" sx={{
fontSize: 11, height: 22, mr: 0.5, mb: 0.5,
                            background: colors.neutral[100], color: colors.neutral[800],
                            border: `1px solid ${colors.neutral[200]}`,
                          }} />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography sx={tableBodySx}>
                        {r.expires_at ? new Date(r.expires_at).toLocaleDateString() : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <GreenSwitch
                        checked={bmExceptionEnabled(r)}
                        disabled={isSavingToggles}
                        onChange={(e) => stageToggleEnabled(r, e.target.checked)}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack alignItems="flex-end" sx={{ width: "100%" }}>
                        <Box
                          onClick={(e) => {
                            e.stopPropagation();
                            setActionMenu({ anchorEl: e.currentTarget, rowId: r.id });
                          }}
                          sx={{ cursor: "pointer", width: 24, height: 24 }}
                        >
                          <LayerdIcon icon={UI_ICONS.more} size={18} color="neutral" />
                        </Box>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
            </>
          )}
        </SiteScopeGate>
      </Stack>

      {/* Row action menu — same anchor + style as the WAF table */}
      <Menu
        anchorEl={actionMenu.anchorEl}
        open={Boolean(actionMenu.anchorEl)}
        onClose={() => setActionMenu({ anchorEl: null, rowId: null })}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{
          top: 13, left: -12,
          "& .MuiList-root": {
            backgroundColor: "white", borderRadius: "8px",
            border: `1px solid ${colors.neutral[200]}`,
            width: 200, boxShadow: "none", py: 0,
          },
          "& .MuiMenu-paper": { boxShadow: "none" },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1}
            onClick={() => {
              const row = rows?.find((r) => r.id === actionMenu.rowId);
              setActionMenu({ anchorEl: null, rowId: null });
              if (row) { setEditing(row); setDrawerOpen(true); }
            }}
            sx={generateMenuSx(176, 37)}
          >
            <LayerdIcon icon={UI_ICONS.edit} size={18} color="neutral" />
            <Typography sx={ { fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>
              Edit
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}
            onClick={() => {
              setDeletingId(actionMenu.rowId);
              setActionMenu({ anchorEl: null, rowId: null });
              setDeleteOpen(true);
            }}
            sx={generateMenuSx(176, 37)}
          >
            <LayerdIcon icon={UI_ICONS.delete} size={18} color="neutral" />
            <Typography sx={ { fontSize: 14, lineHeight: "21px", pl: 1, color: colors.neutral[800] }}>
              Delete
            </Typography>
          </Stack>
        </Box>
      </Menu>

      {/* Delete confirmation Modal — same shape as the WAF delete modal */}
      <Modal open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <Stack
          spacing={4}
          sx={{
            position: "absolute", top: "30%", left: "50%",
            transform: "translate(-50%, -50%)", width: 450,
            backgroundColor: colors.neutral[100], borderRadius: "8px", p: 6,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={3}>
            <Typography sx={ { fontSize: 20, lineHeight: "30px", fontWeight: "medium", color: colors.neutral[1000] }}>
              Delete Exception?
            </Typography>
            <Box onClick={() => setDeleteOpen(false)} sx={{ cursor: "pointer" }}>
              <LayerdIcon icon={UI_ICONS.close} size={20} color="neutral" />
            </Box>
          </Stack>
          <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[700] }}>
            Are you sure you want to permanently delete this BM exception?
          </Typography>
          <Stack direction="row" alignItems="center" spacing={4}>
            <CancelButton onClick={() => setDeleteOpen(false)} outlined />
            <SaveButton onClick={handleDelete} label="Delete" />
          </Stack>
        </Stack>
      </Modal>

      <ExceptionDrawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setEditing(null); }}
        exceptionToEdit={editing}
        onSubmit={submit}
      />
      <StickySaveBar
        open={stickyModifiedCount > 0}
        modifiedCount={stickyModifiedCount}
        modifiedFields={stickyModifiedFields}
        onDiscard={discardToggleChanges}
        onApply={applyToggleChanges}
        isApplying={isSavingToggles}
      />
    </>
  );
};

export default BmExceptionsPage;
